import { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';
const adminFetch = (url, options = {}) => fetch(url, { ...options, credentials: 'include' });

export default function AdminErrors() {
  const [errors, setErrors] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/errors?limit=100`);
      if (!res.ok) {
        toast.error('Impossible de charger le journal d\'erreurs');
        return;
      }
      const data = await res.json();
      setErrors(data.errors || []);
      setTotal(data.total || 0);
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm('Vider tout le journal d\'erreurs ?')) return;
    try {
      const res = await adminFetch(`${API_URL}/api/bloom-gestion-secure/errors`, { method: 'DELETE' });
      if (!res.ok) {
        toast.error('Échec de la suppression');
        return;
      }
      const data = await res.json();
      toast.success(`${data.deleted} entrée(s) supprimée(s)`);
      load();
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const toggle = (i) => setExpanded((prev) => ({ ...prev, [i]: !prev[i] }));

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-errors-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <AlertTriangle className="text-[#C87979]" size={24} />
          <div>
            <h1 className="font-serif text-2xl">Journal des erreurs</h1>
            <p className="text-xs text-[#6B6A68]">Les 500 dernières exceptions serveur non gérées.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B6A68]" data-testid="admin-errors-total">Total : {total}</span>
          <button
            onClick={load}
            disabled={loading}
            className="px-3 py-2 text-xs border border-[#E0DFDD] hover:border-[#1A1514] inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="admin-errors-refresh"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Actualiser
          </button>
          <button
            onClick={clearAll}
            className="px-3 py-2 text-xs bg-red-50 border border-red-200 text-red-700 hover:bg-red-100 inline-flex items-center gap-1.5"
            data-testid="admin-errors-clear"
          >
            <Trash2 size={12} /> Vider
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="animate-spin text-[#6B6A68]" />
        </div>
      ) : errors.length === 0 ? (
        <div className="text-center py-16 text-sm text-[#6B6A68]" data-testid="admin-errors-empty">
          Aucune erreur enregistrée.
        </div>
      ) : (
        <div className="border border-[#E0DFDD] bg-white">
          {errors.map((err, i) => (
            <div key={i} className="border-b border-[#E0DFDD] last:border-b-0" data-testid={`admin-error-row-${i}`}>
              <button
                onClick={() => toggle(i)}
                className="w-full text-left px-4 py-3 hover:bg-[#F9F8F6] transition-colors flex items-start gap-3"
              >
                <div className="pt-0.5 text-[#6B6A68] flex-shrink-0">
                  {expanded[i] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-[#6B6A68]">{err.ts}</span>
                    <span className="text-xs px-1.5 py-0.5 bg-[#F5F4F2] border border-[#E0DFDD] font-medium">{err.method}</span>
                    <span className="font-mono text-xs text-[#1A1514] truncate">{err.path}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    <span className="font-medium text-[#C87979]">{err.exc_type}</span>
                    <span className="text-[#1A1514]">: {err.exc_message}</span>
                  </div>
                </div>
              </button>
              {expanded[i] && (
                <div className="px-8 pb-4 space-y-2 text-xs">
                  {err.query && (
                    <div><span className="text-[#6B6A68]">Query : </span><span className="font-mono">{err.query}</span></div>
                  )}
                  {err.ip && (
                    <div><span className="text-[#6B6A68]">IP : </span><span className="font-mono">{err.ip}</span></div>
                  )}
                  {err.user_agent && (
                    <div><span className="text-[#6B6A68]">UA : </span><span className="text-[#6B6A68]">{err.user_agent}</span></div>
                  )}
                  <div>
                    <div className="text-[#6B6A68] mb-1">Stack :</div>
                    <pre className="bg-[#1A1514] text-[#F5F4F2] p-3 overflow-x-auto whitespace-pre text-[10px] leading-relaxed" data-testid={`admin-error-stack-${i}`}>{err.stack}</pre>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
